<script setup lang="tsx">
import Multiselect from 'vue-multiselect'
import { computed, ref, onBeforeUnmount } from 'vue'
import ClearButton from './ClearButton.vue'
import { riskConfig } from '../risk_management/riskConfig'
import { formatRiskScore, parseRiskScore } from '../risk_management/shared'
import type { Column } from '@/stores/column'

const model = defineModel<Column>({ required: true })
const isOpen = ref(false)

const options = computed(() => {
	const cfg = riskConfig.value
	let prev = 0
	const levels = cfg.riskLevels.slice(0, cfg.matrixSize)
	return levels.map((l) => {
		// Bounds sit half a point outside the whole scores the level covers: rangeMax itself
		// is stored as float32, and sharing an exact bound would match two levels at once.
		const upper = Math.round(formatRiskScore(l.rangeMax, cfg))
		const min = parseRiskScore(prev + 0.5, cfg)
		const max = parseRiskScore(upper + 0.5, cfg)
		prev = upper

		return {
			id: l.rangeMax,
			title: l.title,
			color: l.color,
			min: min,
			max: max,
		}
	})
})

const internalModel = computed({
	get: () => model.value.filter,
	set: (value) => {
		model.value.filter = value
	},
})

onBeforeUnmount(() => (model.value.filter = []))
</script>

<template>
	<Multiselect
		:multiple="true"
		:show-labels="false"
		:hide-selected="true"
		:close-on-select="false"
		:clear-on-select="false"
		:placeholder="model.label + '...'"
		:options="options"
		v-model="internalModel"
		label="title"
		track-by="id"
		@open="isOpen = true"
		@close="isOpen = false"
		:limit="isOpen ? 100 : 3"
	>
		<template #clear>
			<ClearButton right="2rem" v-if="internalModel.length" @click="internalModel = []" />
		</template>

		<template v-if="!isOpen" #limit>
			<strong class="multiselect__strong">{{ $t('misc.limit', [internalModel.length - 3]) }}</strong>
		</template>

		<template #option="{ option }">
			<div class="d-flex align-items-center gap-2">
				<span class="badge px-2" :style="'background-color:' + option.color">{{ option.title }}</span>
			</div>
		</template>
	</Multiselect>
</template>
